<script setup>
import {ref, computed} from 'vue';
import {useUserStore} from '~/store/userStore.js';
import Text from '@/components/atoms/Text.vue';
import ProductPrice from '@/components/molecules/ProductPrice.vue';
import Image from '~/components/atoms/Image.vue';
import ProductLabels from '~/components/molecules/ProductLabels.vue';
import {stripHtml} from '~/utils/stripHtml.js';

const props = defineProps({
  adminInfo: Object,
  nameWidth: String,
  nameHeight: String,
  nameAlign: {
    type: String,
    default: 'left',
  },
  mainImage: String,
  mobileImage: String,
  secondImage: String,
  name: {
    type: String,
    required: true,
  },
  sku: String,
  price: [Number, String],
  status: [String, Object],
  unit: {
    type: String,
    required: true,
  },
  oldPrice: Number,
  discount: Number,
  labels: {
    type: Array,
    default: () => [],
  },
  slug: {
    type: String,
    required: true,
  },
  anotherProducts: Array,
  padding: {
    type: String,
    default: '0',
  },
  lazy: {
    type: Boolean,
    default: true,
  },
  restparti_qty: Number,
  restparti_unit_block: String,
});

const userStore = useUserStore();
const activeColor = ref(null);
const plainName = computed(() => stripHtml(props.name));

const changeActiveColor = (index) => {
  activeColor.value = index;
};

const secondPreview = computed(() => {
  const hovered = props.anotherProducts?.[activeColor.value];
  if (hovered?.image) {
    return hovered.image;
  }
  return props.secondImage || null;
});

const showHover = computed(() => !!(secondPreview.value || props.anotherProducts?.length));

const emit = defineEmits(['click', 'anotherColorClick'])

const onPreviewClick = () => {
  emit('click')
}

// The swatch links are nested inside the main product link, so without stopping
// propagation a swatch click would be attributed to the parent product.
const onAnotherColorClick = (product, index) => {
  emit('anotherColorClick', {product, index})
}
</script>

<template>
  <div class="pp">
    <nuxt-link :to="'/shop/' + slug" @click="onPreviewClick" @auxclick="onPreviewClick">
      <div class="product-preview" :style="{ padding }">
        <div class="image">
          <div class="img">
            <Image
                :loading="lazy ? 'lazy' : 'eager'"
                :mobile-src="mobileImage"
                :src="mainImage"
                :alt="plainName"
            />
          </div>
          <ProductLabels :right-direction="true" class="label" :labels="labels"/>
        </div>

        <div class="hover" v-if="showHover && $device.isDesktop">
          <div class="img no-flex">
            <Image
                :loading="lazy ? 'lazy' : 'eager'"
                class="second-image"
                :src="secondPreview || mainImage"
                :alt="plainName"
            />
          </div>

          <client-only>
            <div class="another-colors row g-8" @mouseleave="activeColor = null" v-if="anotherProducts?.length">
              <nuxt-link
                  :to="'/shop/' + product.url"
                  class="another-color-product-image hbb"
                  @mouseenter="changeActiveColor(index)"
                  @click.stop="onAnotherColorClick(product, index)"
                  @auxclick.stop="onAnotherColorClick(product, index)"
                  :class="{ active: activeColor == index }"
                  v-for="(product, index) in anotherProducts.slice(0, 4)"
                  :key="index"
              >
                <img :src="product.image" alt=""/>
              </nuxt-link>
            </div>
          </client-only>
        </div>

        <div class="p-mobile-padding g-8">
          <Text
              class="name"
              :style="{ width: nameWidth, height: nameHeight, textAlign: nameAlign ? nameAlign : '' }"
              text-alignment="left"
              v-html="name"
          ></Text>
          <div class="bottom-info row g-8" v-if="price">
            <template v-if="status === 'Restparti'">
              <ProductPrice :force-unit="true" :unit="restparti_unit_block" :digits="0"
                            :price="Math.ceil(price / restparti_qty)"/>
              <Text class="discount" tag="b" type="red">
                {{ restparti_qty }} <span v-html="restparti_unit_block"></span>
              </Text>
            </template>
            <template v-else>
              <ProductPrice :unit="unit" :digits="0" :price="price"/>
              <ProductPrice style="font-weight: 500;" v-if="discount > 0 && oldPrice > price" :digits="0" type="old"
                            :unit="unit"
                            :price="oldPrice"/>
              <Text class="discount" tag="b" type="red" v-if="discount > 0">-{{ discount }}%</Text>
            </template>
          </div>
        </div>

        <div v-if="adminInfo && userStore.isAdmin && userStore.showAdmin">
          <div class="row space-between full-width flex-start"
               style=" font-size: 10px; font-weight: 600; color: #707070;">
            <table>
              <tbody>
              <tr>
                <td style="width: 100px;">IS:</td>
                <td>{{ adminInfo.in_stock }}</td>
              </tr>
              <tr>
                <td>RS:</td>
                <td>{{ adminInfo.ready_to_sell }}</td>
              </tr>
              <tr>
                <td>SI:</td>
                <td>{{ adminInfo.samples_in_stock }}</td>
              </tr>
              <tr>
                <td>SA:</td>
                <td>{{ adminInfo.samples_available }}</td>
              </tr>
              </tbody>
            </table>
            <table>
              <tbody>
              <tr>
                <td style="width: 100px;">SS:</td>
                <td>{{ adminInfo.sorting_score }}</td>
              </tr>
              <tr>
                <td>SA:</td>
                <td>{{ adminInfo.sold_all_time }}</td>
              </tr>
              <tr>
                <td>SKU:</td>
                <td>{{ sku }}</td>
              </tr>
              <tr>
                <td>PPA</td>
                <td>{{ adminInfo.promotional_status ? 'YES' : 'NO' }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </nuxt-link>
  </div>
</template>

<style scoped lang="scss">
.another-color-product-image {
  display: flex;
  width: 44px;
  height: 44px;
  flex: none;
  overflow: hidden;

  img {
    width: 44px;
    height: 44px;
    object-fit: cover;
  }
}

.discount {
  @include smd {
    font-size: 12px;
  }
}

.img {
  aspect-ratio: 1/1;
  width: 100%;
  max-height: 100%;
  position: relative;
  overflow: hidden;
}

a {
  @include smd {
    // overflow: hidden;
  }
}

.product-preview {
  gap: 5px;
}

.label {
  position: absolute;
  bottom: 8px;
  left: -10px;
  z-index: 9999;

  @include smd {
    bottom: 10px;
    left: 0px;
  }
}

.price {
  @include smd {
    font-size: 12px;
  }

  &.old {
    @include smd {
      font-size: 10px;
    }
  }
}

.name {
  font-size: 13px;

  @include smd {
    font-size: 12px;
    height: auto;
    min-height: 32px;
  }
}

.p-mobile-padding {
  @include mobile {
    padding-left: 12px !important;
    padding-right: 0 !important;
  }
}

.pp:nth-child(2n) {
  .p-mobile-padding {
    @include mobile {
      padding-left: 0 !important;
      padding-right: 12px !important;
    }
  }
}

.hover {
  position: absolute;
  top: 0;
  bottom: 0;
  box-shadow: 0 32px 48px rgba(82, 82, 82, .08);
  background-color: $white;
  z-index: 9;
  left: -5px;
  right: -5px;
  opacity: 0;
  transition: all .25s ease-in-out;
  padding: 0 12px;
  overflow: hidden;
}

.product-preview:hover .hover {
  opacity: 1;
}

.another-colors {
  margin: auto;
}
</style>
